const VisualComponent = require('./VisualComponent');
const CollapsibleSectionNavigation = require('./helpers/CollapsibleSectionNavigation');
const AutoScrollMenu = require('./helpers/AutoScrollSectionNavigation');

class SectionsNavbar extends VisualComponent {
    constructor(data, articleView, config) {
        super();
        this.articleView = articleView;
        this.config = config.sectionsNavbar;
        this.breakpoint = config.mobileBreakpoint - 1;

        this.menuTrigger = this.getSectionsNavigationTrigger();
        const sections = this.getSectionsNavigation();
        if (!sections) {
            console.debug("Sections navigation HTML hasn't been found. Constructor stopped.");
            this.menuTrigger && this.disableAccessibly(this.menuTrigger);
            return;
        }
        this.main = sections;
        this.data = data;
        this.visibleParts = new Set();
        this.displayBreakpoint = getComputedStyle(document.documentElement)
            .getPropertyValue('--nav-section-display-bp')
            .match(/\d+/)[0];
    }

    initiate() {
        if (!this.main) return;
        try {
            this.prepareContent();
            this.setObserver();
            this.setScrollIndicator(this.config.scrollIndicator, this.list);
            this.addEventListeners();
            this.collapseSections();
            this.autoScrollMenu();
        } catch (e) {
            console.debug('Section Navigation - initiate: ' + e);
        }
    }

    prepareContent() {
        const parentNode = this.menuTrigger.parentNode;
        if (parentNode === this.main.parentNode) {
            parentNode.classList.add('core-sections-menu');
        }
        this.hasDynamicSections(this.config.dynamicSections);
        this.markBackmatterStart();
        this.addWrappedDynamicSections(this.config.dynamicSections.wrapped);
        this.markIfNoContents();
        this.applyAccessibilityRules();
    }
    addEventListeners() {
        this.containIntoView();
        if (this.config.containIntoView) {
            this.articleView.subscribe('scroll', this.containIntoView.bind(this));
            this.articleView.subscribe('resize', this.containIntoView.bind(this));
        }
        this.articleView.subscribe('scroll', this.animatingMenu.bind(this));
        this.articleView.subscribe('resize', this.updateScrollIndicator.bind(this));

        const that = this;
        const observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
                    that.animatingMenu();
                }
            });
        });
        observer.observe(this.menuTrigger, {attributes: true});

        this.addNavMenuListeners();
    }

    addNavMenuListeners() {
        const sectionsLinks = this.main.querySelectorAll('a');
        [this.menuTrigger, ...sectionsLinks].forEach(e =>
            e.addEventListener('click', () => this.toggleNavMenu(), true)
        );
        this.menuTrigger.addEventListener('keydown', this.onMenuButtonKeydown.bind(this));
        sectionsLinks.forEach(e => e.addEventListener('keydown', this.onMenuLinkKeydown.bind(this)));
        sectionsLinks[sectionsLinks.length - 1].addEventListener('keydown', this.onLastMenuLinkKeydown.bind(this));
        window.addEventListener('pointerdown', this.onBackgroundPointerdown.bind(this), true);
        document.body.addEventListener('smartResize', () => this.isNavMenuOpen() && this.toggleNavMenu());
        this.main.addEventListener('transitionend', e => {
            if (e.target.id !== this.main.id || this.isNavMenuOpen() || this.#isDesktopView()) return;
            this.hideNavMenu();
        });
        window.addEventListener('resize', this.onScreenSizeChange.bind(this));
    }

    toggleNavMenu(menuTrigger = this.menuTrigger) {
        const isNavMenuOpen = this.isNavMenuOpen();
        !isNavMenuOpen && this.displayNavMenu();
        menuTrigger.setAttribute('aria-expanded', !isNavMenuOpen);
        menuTrigger.parentNode.classList.toggle('open');
        document.body.classList.toggle('navbar-menu-open');
    }

    isNavMenuOpen() {
        return this.menuTrigger.getAttribute('aria-expanded') === 'true';
    }

    onBackgroundPointerdown(event) {
        if (!this.isNavMenuOpen() || this.menuTrigger.parentElement.contains(event.target)) return;
        this.toggleNavMenu();
    }

    onMenuButtonKeydown(event) {
        if (event.key === 'Tab' && this.isNavMenuOpen() && event.shiftKey) this.toggleNavMenu();
    }

    onLastMenuLinkKeydown(event) {
        if (event.key === 'Tab' && this.isNavMenuOpen() && !event.shiftKey) this.toggleNavMenu();
    }

    onMenuLinkKeydown(event) {
        if (!this.isNavMenuOpen()) return;
        if (event.key === 'Esc' || event.key === 'Escape') this.toggleNavMenu();
    }

    onScreenSizeChange() {
        if (this.#isDesktopView()) this.displayNavMenu();
    }

    displayNavMenu() {
        this.main.childNodes.forEach(node => (node.style.display = 'block'));
    }

    hideNavMenu() {
        this.main.childNodes.forEach(node => (node.style.display = 'none'));
    }

    #isDesktopView() {
        return window.innerWidth >= this.displayBreakpoint;
    }

    setObserver(depth = 2) {
        const list = this.main.querySelector('ul');
        if (!list) return;

        let extraMarginValue = 0;
        let totalBottomStyleValue = 0;
        let totalStyleValue = 0;
        // get the maximum value of (margin padding bottom of the previous section + margin padding top of the current section)
        // and use the value to increase root margin
        if (this.config.observer.hasDynamicRootMargin) {
            this.data.sections.forEach(section => {
                totalStyleValue =
                    totalBottomStyleValue + this.calculateSummedMarginPadding(section.sectionParent, 'top');
                if (extraMarginValue < totalStyleValue) extraMarginValue = totalStyleValue;
                if (section.sectionParent) {
                    totalBottomStyleValue = this.calculateSummedMarginPadding(section.sectionParent, 'bottom');
                } else {
                    totalBottomStyleValue = 0;
                }
            });
        }

        this.data.sections.forEach(section => {
            const parentId = this.getParentId(section.sectionParent, depth);
            section.sectionParent.navbarPointer = list.querySelector(`[href="#${parentId}"]`);
            this.observe(section.sectionParent, extraMarginValue);
        });
        this.list = list;
    }

    calculateSummedMarginPadding(element, position) {
        let computedStyle = window.getComputedStyle(element);
        let marginValue = computedStyle.getPropertyValue(`margin-${position}`);
        let paddingValue = computedStyle.getPropertyValue(`padding-${position}`);

        let margin = marginValue === 'auto' || !marginValue ? 0 : parseFloat(marginValue) || 0;
        let padding = paddingValue === 'auto' || !paddingValue ? 0 : parseFloat(paddingValue) || 0;

        return Math.max(0, margin) + Math.max(0, padding);
    }

    getParentId(node, depth) {
        if (node.id || depth === 0) {
            return node.id;
        } else if (node.parentNode) {
            return this.getParentId(node.parentNode, depth - 1);
        } else {
            return null;
        }
    }

    updateScrollIndicator() {
        this.scrollIndicator?.markInnerScroll({target: this.list});
    }
    observe(elem, stylesValue) {
        if (typeof window.IntersectionObserver !== 'undefined') {
            if (!this.observer) {
                let scrollPaddingTop = window
                    .getComputedStyle(document.documentElement)
                    .getPropertyValue('scroll-padding-top');
                const observerOptionsWithMargin = this.getObserverOptionsWithMargin(scrollPaddingTop, stylesValue);
                this.observer = new IntersectionObserver(
                    this.onIntersectionChange.bind(this),
                    observerOptionsWithMargin
                );
                this.observer = new IntersectionObserver(
                    this.onIntersectionChange.bind(this),
                    observerOptionsWithMargin
                );
            }
            this.observer.observe(elem);
        }
    }

    getObserverOptionsWithMargin(scrollPaddingTop, stylesValue) {
        const observerConfig = this.config.observer;
        if (observerConfig.hasDynamicRootMargin && stylesValue) {
            let scrollPaddingTopValue =
                scrollPaddingTop === 'auto' || !scrollPaddingTop ? 0 : parseFloat(scrollPaddingTop) || 0;
            let totalValue = scrollPaddingTopValue + stylesValue;

            return {
                rootMargin: `-${totalValue}px 0px 0px 0px`,
                ...observerConfig.options,
            };
        } else {
            return scrollPaddingTop !== 'auto'
                ? observerConfig.customRootMargin !== '0px'
                    ? {
                          rootMargin: observerConfig.customRootMargin,
                          ...observerConfig.options,
                      }
                    : {
                          rootMargin: `-${scrollPaddingTop} 0px 0px 0px`,
                          ...observerConfig.options,
                      }
                : observerConfig.options;
        }
    }

    onIntersectionChange(entries) {
        let activeSection;
        let finalSections = new Set();
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                this.visibleParts.add(entry.target);
            } else {
                this.visibleParts.delete(entry.target);
            }
            finalSections.clear();
            let tempSectionsSet = new Set(this.visibleParts);
            for (let i = 2; i <= 6; i++) {
                let section = this.getActiveSections(tempSectionsSet);
                if (section) {
                    tempSectionsSet = this.cleanHeadingElementsWithTheSameLevel(tempSectionsSet, i);
                    if (finalSections.size === 0) {
                        finalSections.add(section);
                        activeSection = section;
                    } else if (this.isPartOfSet([...finalSections][0], section)) {
                        finalSections.add(section);
                        activeSection = section;
                    }
                }
            }
        });

        this.data.sections
            .map(s => s.sectionParent)
            .forEach(section => {
                if (activeSection && activeSection === section) {
                    section.navbarPointer?.setAttribute('aria-current', 'true');
                } else {
                    section.navbarPointer?.setAttribute('aria-current', 'false');
                }
            });
    }

    isPartOfSet(mainSection, subSection) {
        return subSection.id ? mainSection.querySelector('#' + subSection.id) : false;
    }

    cleanHeadingElementsWithTheSameLevel(set, hLevel) {
        const elementsToDelete = [];
        const selector = 'h' + hLevel;
        set.forEach(section => {
            if (section.querySelector(selector)) {
                elementsToDelete.push(section);
            }
        });
        elementsToDelete.forEach(section => {
            set.delete(section);
        });
        return set;
    }

    getActiveSections(sections) {
        return sections.size && sections.size > 1
            ? [...sections].reduce((prev, curr) => {
                  return curr.getBoundingClientRect().top < prev.getBoundingClientRect().top ? curr : prev;
              })
            : sections.values().next().value;
    }

    getSectionsNavigation() {
        return this.articleView.getArticle().querySelector(this.config.id);
    }
    getSectionsNavigationTrigger() {
        return this.articleView.getArticle().querySelector(this.config.trigger);
    }

    markBackmatterStart() {
        if (!this.config?.markBackmatterStart) return;

        const anchorList = this.main.querySelectorAll('a[href]');
        let found = false;
        anchorList.forEach((anchor, i) => {
            if (anchor.hash) {
                if (!!document.body.querySelector(anchor.hash)?.closest('[data-extent="backmatter"]') && !found) {
                    if (i !== 0) anchor.parentElement.classList.add('bordered');
                    found = true;
                }
            }
        });
    }
    markIfNoContents() {
        if (this.checkIfEmpty(this.main)) {
            this.disableAccessibly(this.main);
            this.disableAccessibly(this.menuTrigger);
        }
    }

    /**
     * @param {object} [params] - The configuration object.
     * @param {string} [params.enabled=false] - if the functionality is enabled or not
     * @param {Object[]} [params.list] - the array of custom sections we want to include in the section navigation
     * @param {string} [params.list[].perpendText=undefined] - If we want to prepend a custom text for the new link
     * @param {string} [params.list[].customText=undefined] - If we want an element to provide the custom text for the link
     * @param {string} [params.list[].parentId=undefined] - the ID of the parent of the element that we want to add
     * @param {string} [params.list[].selector=h2] - the HTML selector of the element we want to target
     */
    hasDynamicSections(params) {
        if (!this.config.dynamicSections.enabled) return;

        params.list.forEach(obj => {
            const parent = document.getElementById(obj.parentId);
            if (!parent) return;

            const element = parent.querySelector(obj.selector || 'h2');
            const text = [obj.perpendText, eval(obj.customText) || element?.innerHTML].join('');
            if (text === '') return;
            this.insertLiElement(parent, text);
            const dynamicSection = {
                sectionParent: parent,
                parentNode: {id: obj.parentId},
            };
            this.data.sections.push(dynamicSection);
        });
    }

    addWrappedDynamicSections(params) {
        if (!params.enabled || !params.wrappersSelector) return;

        const parentWrappers = this.articleView.getArticle().querySelectorAll(params.wrappersSelector);

        let sections = [];
        parentWrappers.forEach(wrapper => sections.push(...wrapper.querySelectorAll('section')));
        if (!sections.length) return;
        sections.forEach(section => {
            const element = section.querySelector('h2');
            if (!element) return;
            const text = element.innerHTML;
            if (text === '') return;
            if (!section.id)
                section.id =
                    'core-' +
                    text
                        .toLowerCase()
                        .replaceAll(' ', '-')
                        .replace(/[^\w-]/g, '');
            this.insertLiElement(section, text);
            const dynamicSection = {
                sectionParent: section,
                parentNode: {id: section.id},
            };
            this.data.sections.push(dynamicSection);
        });
    }

    insertLiElement(dynamicSection, text) {
        let list = this.main.querySelector('ul');
        if (!list) {
            list = document.createElement('ul');
            const header = this.main.querySelector('header');
            header ? header.after(list) : this.main.prepend(list);
        }
        if (!list) return;

        const link = document.createElement('a');
        link.href = '#' + dynamicSection.id;
        link.innerHTML = text;

        const newLi = document.createElement('li');
        newLi.appendChild(link);
        const articleSections = [
            ...document.body.querySelectorAll(this.config.dynamicSections.insertionSectionsSelector),
        ];
        const indexOfDynamicSection = articleSections.indexOf(dynamicSection);
        for (let li of list.children) {
            if (
                articleSections.indexOf(document.body.querySelector(li.querySelector('a[href]')?.hash)) >
                indexOfDynamicSection
            ) {
                li.before(newLi);
                return;
            }
        }
        list.appendChild(newLi);
    }
    containIntoView() {
        if (!this.config.containIntoView || !this.main) return;
        this.restrainIntoView(this.main, 64, this.main.previousElementSibling);
    }
    animatingMenu() {
        if (this.config.animateMenu) {
            // LIT-498729 | Improve positioning of section navigation menu & pill
            const {bottom} = this.main.getBoundingClientRect();
            if (window.innerHeight < bottom) {
                this.cssVar_set('--sections-menu-translate-y', window.innerHeight - bottom - 16 + 'px');
            } else {
                this.cssVar_set('--sections-menu-translate-y', 0);
            }
        }
    }

    applyAccessibilityRules() {
        const header = this.main.querySelector('header');
        if (header) {
            header.setAttribute('aria-hidden', 'true');
        }
    }

    collapseSections() {
        if (!this.config.collapseSections.enabled) return;

        try {
            const sections = this.articleView.getArticle().querySelector('#article_sections_menu > ul');
            const sectionNavigation = new CollapsibleSectionNavigation(sections, this.config.collapseSections);
            sectionNavigation.init();
        } catch (e) {
            console.debug('Section Navigation - collapse Sections: ' + e);
        }
    }

    autoScrollMenu() {
        if (!this.config.enableAutoScrollMenu) return;

        try {
            const sections = this.articleView.getArticle().querySelector('#article_sections_menu > ul');
            new AutoScrollMenu(sections);
        } catch (e) {
            console.debug('Section Navigation - auto scroll: ' + e);
        }
    }
}

module.exports = SectionsNavbar;
